import React, { useState, useEffect } from 'react';
import { parseSaveError, parseSaveWarnings } from '../api/validationErrors';
import styled from '@emotion/styled';
import axios from 'axios';
import SuccessModal from './SuccessModal';

const ClinicalMeasurementsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 12px;
  width: 100%;
`;

const SectionTitle = styled.h2`
  width: 100%;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 17px;
  display: flex;
  align-items: center;
  color: #6D6D71;
`;

const ContentBox = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 16px;
  gap: 20px;
  width: 100%;
  background: #FFFFFF;
  border: 1px solid #C5C5C6;
  border-radius: 12px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 6px;
  width: ${props => props.fullWidth ? '100%' : 'calc(33.33% - 11px)'};
  min-width: 150px;
`;

const FormLabel = styled.label`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;
  gap: 4px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  color: #2C2C2D;
`;

const FormInput = styled.input`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 12px 16px;
  gap: 10px;
  width: 100%;
  height: 40px;
  background: #FFFFFF;
  /* Red when the server rejected this field, amber for a soft-range confirm.
     Same tones as the Echocardiogram section. */
  border: 1px solid ${props => (props.hasError ? '#D0342C' : props.hasWarning ? '#C77700' : '#A7A7AA')};
  border-radius: 12px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 19px;
  color: #2C2C2D;

  &::placeholder {
    color: #A7A7AA;
  }
`;

// Same tones as the Echocardiogram section, so an error looks the same wherever
// the operator meets it.
const FieldMessage = styled.div`
  font-family: 'Sofia Sans', sans-serif;
  font-size: 12px;
  line-height: 15px;
  margin-top: 4px;
  color: ${props => (props.tone === 'error' ? '#D0342C' : props.tone === 'warning' ? '#C77700' : '#6D6D71')};
`;

const Banner = styled.div`
  box-sizing: border-box;
  width: 100%;
  margin-bottom: 16px;
  padding: 12px 16px;
  border-radius: 8px;
  font-family: 'Sofia Sans', sans-serif;
  font-size: 13px;
  line-height: 18px;
  border: 1px solid ${props => (props.tone === 'error' ? '#F0C4C1' : props.tone === 'warning' ? '#F0DCB8' : '#C9DDF0')};
  background: ${props => (props.tone === 'error' ? '#FDF3F2' : props.tone === 'warning' ? '#FFF9EF' : '#F3F8FC')};
  color: ${props => (props.tone === 'error' ? '#8C221C' : props.tone === 'warning' ? '#7A4A00' : '#1C5C94')};

  ul {
    margin: 6px 0 0;
    padding-left: 18px;
  }
`;

const InputGroup = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  width: 100%;
`;

const SaveButton = styled.button`
  display: flex;
  width: 230px;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 15px 15px;
  gap: 12px;
  background: #2575BD;
  border: none;
  border-radius: 16px;
  font-family: 'Sofia Sans', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 22px;
  text-align: center;
  color: #FFFFFF;
  cursor: pointer;
  margin-top: 20px;
  &:hover {
    background-color: ${props => props.disabled ? '#E0F0FF' : '#1c5c94'};
  }
`;

const ClinicalMeasurements = ({ participantId, formData, onFormDataChange, onSaveSuccess }) => {
  const [data, setData] = useState({
    height: '',
    weight: '',
    bmi: '',
    waist_circumference: '',
    pulse_rate: ['', '', '', ''],
    blood_pressure: {
      systolic: ['', '', '', ''],
      diastolic: ['', '', '', '']
    },
    temperature: '',
    spo2: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Populated from the server's 422 field_errors / 200 warnings, so the form shows
  // exactly what the API decided rather than a second guess at it.
  const [fieldErrors, setFieldErrors] = useState({});
  const [fieldWarnings, setFieldWarnings] = useState({});
  const [saveError, setSaveError] = useState('');
  const [otherErrors, setOtherErrors] = useState([]);

  // No `units` is sent. The site records °C and cm only, which are already the
  // server's storage units, so declaring them adds nothing — R-1.4 is satisfied
  // regardless: validation records field_units as {temperature: "°C"},
  // {waist_circumference: "cm"} whether or not the client declares a unit.
  //
  // It was actively harmful to send one. The declared token has to match the
  // storage unit exactly, and for temperature that is "°C" WITH the degree sign;
  // "C" is not in alt_units (which holds only "F"), so the server rejected on
  // unknown_unit before it ever range-checked the value. That hid the real error:
  // 99 is out of the 30–45 range, and the server's own message already says so and
  // gives the conversion ("99 °F = 37.2 °C").

  useEffect(() => {
    const fetchClinicalMeasurements = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}participant/get-participant-info?participant_id=${participantId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data && response.data.clinical_measurements) {
          const apiData = response.data.clinical_measurements;
          const initialData = {
            height: apiData.height || '',
            weight: apiData.weight || '',
            bmi: apiData.bmi || '',
            waist_circumference: apiData.waist_circumference || '',
            pulse_rate: apiData.pulse_rate || ['', '', '', ''],
            blood_pressure: {
              systolic: apiData.blood_pressure?.systolic || ['', '', '', ''],
              diastolic: apiData.blood_pressure?.diastolic || ['', '', '', '']
            },
            temperature: apiData.temperature || '',
            spo2: apiData.spo2 || ''
          };
          
          setData(initialData);
          onFormDataChange(initialData);
        }
      } catch (error) {
        console.error('Error fetching clinical measurements:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchClinicalMeasurements();
  }, [participantId]);

  const calculateBMI = (height, weight) => {
    if (height && weight) {
      const heightInMeters = height / 100;
      return (weight / (heightInMeters * heightInMeters)).toFixed(1);
    }
    return '';
  };

  const handleInputChange = (field, value, index = null) => {
    let updatedData = { ...data };
    
    if (index !== null) {
      if (Array.isArray(updatedData[field])) {
        updatedData[field][index] = value;
      } else if (typeof updatedData[field] === 'object') {
        updatedData[field][index] = value;
      }
    } else {
      updatedData[field] = value;
      
      // Calculate BMI when height or weight changes
      if (field === 'height' || field === 'weight') {
        updatedData.bmi = calculateBMI(
          field === 'height' ? value : data.height,
          field === 'weight' ? value : data.weight
        );
      }
    }
    
    setData(updatedData);
    onFormDataChange(updatedData);
  };

  const calculateAverage = (values) => {
    const validValues = values.slice(0, 3).filter(value => value !== '').map(Number);
    return validValues.length === 3 ? Math.round(validValues.reduce((a, b) => a + b) / 3) : '';
  };

  const calculateBPAverage = (values) => {
    const validValues = values.slice(1, 3).filter(value => value !== '').map(Number);
    return validValues.length === 2 ? Math.round(validValues.reduce((a, b) => a + b) / 2) : '';
  };

  const updatePulseRate = (newPulseRate) => {
    const average = calculateAverage(newPulseRate);
    return [...newPulseRate.slice(0, 3), average];
  };

  const updateBloodPressure = (type, newValue) => {
    const average = calculateBPAverage(newValue);
    return [...newValue.slice(0, 3), average];
  };

  const handlePulseRateChange = (index, value) => {
    setData(prevData => {
      const newPulseRate = [...prevData.pulse_rate];
      newPulseRate[index] = value;
      return { ...prevData, pulse_rate: updatePulseRate(newPulseRate) };
    });
  };

  const handleBloodPressureChange = (type, index, value) => {
    setData(prevData => {
      const newBloodPressure = { ...prevData.blood_pressure };
      newBloodPressure[type][index] = value;
      newBloodPressure[type] = updateBloodPressure(type, newBloodPressure[type]);
      return { ...prevData, blood_pressure: newBloodPressure };
    });
  };

  const handleSave = async () => {
    setFieldErrors({});
    setFieldWarnings({});
    setOtherErrors([]);
    setSaveError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}participant/update-clinicals`,
        {
          participant_id: participantId,
          clinical_measurements: data
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // R-1.3 — soft-range findings save, but come back to be confirmed in amber.
      const warnings = parseSaveWarnings(response.data, 'clinical_measurements');
      setFieldWarnings(warnings);
      if (Object.keys(warnings).length > 0) {
        setSaveError('');
        setIsModalOpen(true);
      } else {
        setIsModalOpen(true);
      }
    } catch (error) {
      // A 422 means nothing was persisted, so the entered values stay on screen and
      // the specific fields are called out. Previously this branch only logged to
      // the console and the operator saw no change whatsoever.
      const parsed = parseSaveError(error, 'clinical_measurements');
      setFieldErrors(parsed.fieldErrors);
      setOtherErrors(parsed.unmatched);
      setSaveError(parsed.summary);
      console.error('Error saving clinical measurements:', parsed.status, error);
    }
  };

  // Renders the server's verdict for one field, plus any local hint.
  const messagesFor = (field) => {
    const error = fieldErrors[field];
    const warning = fieldWarnings[field];
    return (
      <>
        {error && <FieldMessage tone="error">{error.message}</FieldMessage>}
        {!error && warning && <FieldMessage tone="warning">{warning}</FieldMessage>}
      </>
    );
  };

  const hasError = (field) => Boolean(fieldErrors[field]);

  const handleModalClose = () => {
    setIsModalOpen(false);
    onSaveSuccess();
  };

  if (isLoading) {
    return <div>Loading clinical measurements...</div>;
  }

  return (
    <ClinicalMeasurementsContainer>
      <SectionContainer>
        <SectionTitle>CLINICAL MEASUREMENTS</SectionTitle>
        <ContentBox>
          {saveError && (
            <Banner tone="error">
              {saveError}
              {/* Every message is listed here as well as inline. Inline placement
                  depends on the field having a rendered input, and the server can
                  flag a path this form does not render one for (an array index, or a
                  cross-field rule naming two fields). Listing them all means no
                  rejection can be invisible, which is the failure this replaced. */}
              {(() => {
                const all = [
                  ...Object.values(fieldErrors).map((e) => e.message),
                  ...otherErrors
                ].filter(Boolean);
                const unique = [...new Set(all)];
                // A single message is already shown as the summary above.
                if (unique.length <= 1) return null;
                return (
                  <ul>
                    {unique.map((message, index) => <li key={index}>{message}</li>)}
                  </ul>
                );
              })()}
            </Banner>
          )}
          {!saveError && Object.keys(fieldWarnings).length > 0 && (
            <Banner tone="warning">
              Saved. {Object.keys(fieldWarnings).length === 1
                ? 'One value is outside the expected range — please confirm the field highlighted in amber.'
                : `${Object.keys(fieldWarnings).length} values are outside the expected range — please confirm the fields highlighted in amber.`}
            </Banner>
          )}
          <InputGroup>
            <FormField>
              <FormLabel>Height (cm)</FormLabel>
              <FormInput
                type="number"
                value={data.height}
                hasError={hasError('height')}
                hasWarning={Boolean(fieldWarnings.height)}
                onChange={(e) => handleInputChange('height', e.target.value)}
                placeholder="Enter height"
              />
              {messagesFor('height')}
            </FormField>

            <FormField>
              <FormLabel>Weight (kg)</FormLabel>
              <FormInput
                type="number"
                value={data.weight}
                hasError={hasError('weight')}
                hasWarning={Boolean(fieldWarnings.weight)}
                onChange={(e) => handleInputChange('weight', e.target.value)}
                placeholder="Enter weight"
              />
              {messagesFor('weight')}
            </FormField>

            <FormField>
              <FormLabel>BMI</FormLabel>
              <FormInput
                type="number"
                value={data.bmi}
                readOnly
                hasError={hasError('bmi')}
                hasWarning={Boolean(fieldWarnings.bmi)}
                placeholder="Calculated BMI"
              />
              {messagesFor('bmi')}
            </FormField>
          </InputGroup>

          <InputGroup>
            <FormField>
              <FormLabel>Waist Circumference (cm)</FormLabel>
              <FormInput
                type="number"
                value={data.waist_circumference}
                hasError={hasError('waist_circumference')}
                hasWarning={Boolean(fieldWarnings.waist_circumference)}
                onChange={(e) => handleInputChange('waist_circumference', e.target.value)}
                placeholder="Enter waist circumference"
              />
              {messagesFor('waist_circumference')}
            </FormField>
          </InputGroup>

          <InputGroup>
            {[0, 1, 2, 3].map((index) => (
              <FormField key={index}>
                <FormLabel>{index === 3 ? 'Average Pulse Rate' : `Pulse Rate ${index + 1}`}</FormLabel>
                <FormInput
                  type="number"
                  value={data.pulse_rate[index]}
                  onChange={(e) => handlePulseRateChange(index, e.target.value)}
                  placeholder={index === 3 ? 'Average' : `Pulse ${index + 1}`}
                  readOnly={index === 3}
                />
              </FormField>
            ))}
          </InputGroup>

          <InputGroup>
            <FormField>
              <FormLabel>Blood Pressure (mmHg)</FormLabel>
              <InputGroup>
                <FormField>
                  <FormLabel>Systolic 1</FormLabel>
                  <FormInput
                    type="number"
                    value={data.blood_pressure.systolic[0]}
                    onChange={(e) => handleBloodPressureChange('systolic', 0, e.target.value)}
                    placeholder="Systolic 1"
                  />
                </FormField>
                <FormField>
                  <FormLabel>Diastolic 1</FormLabel>
                  <FormInput
                    type="number"
                    value={data.blood_pressure.diastolic[0]}
                    onChange={(e) => handleBloodPressureChange('diastolic', 0, e.target.value)}
                    placeholder="Diastolic 1"
                  />
                </FormField>
              </InputGroup>
            </FormField>
          </InputGroup>

          <InputGroup>
            <FormField>
              <InputGroup>
                <FormField>
                  <FormLabel>Systolic 2</FormLabel>
                  <FormInput
                    type="number"
                    value={data.blood_pressure.systolic[1]}
                    onChange={(e) => handleBloodPressureChange('systolic', 1, e.target.value)}
                    placeholder="Systolic 2"
                  />
                </FormField>
                <FormField>
                  <FormLabel>Diastolic 2</FormLabel>
                  <FormInput
                    type="number"
                    value={data.blood_pressure.diastolic[1]}
                    onChange={(e) => handleBloodPressureChange('diastolic', 1, e.target.value)}
                    placeholder="Diastolic 2"
                  />
                </FormField>
              </InputGroup>
            </FormField>
          </InputGroup>

          <InputGroup>
            <FormField>
              <InputGroup>
                <FormField>
                  <FormLabel>Systolic 3</FormLabel>
                  <FormInput
                    type="number"
                    value={data.blood_pressure.systolic[2]}
                    onChange={(e) => handleBloodPressureChange('systolic', 2, e.target.value)}
                    placeholder="Systolic 3"
                  />
                </FormField>
                <FormField>
                  <FormLabel>Diastolic 3</FormLabel>
                  <FormInput
                    type="number"
                    value={data.blood_pressure.diastolic[2]}
                    onChange={(e) => handleBloodPressureChange('diastolic', 2, e.target.value)}
                    placeholder="Diastolic 3"
                  />
                </FormField>
              </InputGroup>
            </FormField>
          </InputGroup>

          <InputGroup>
            <FormField>
              <InputGroup>
                <FormField>
                  <FormLabel>Systolic Avg</FormLabel>
                  <FormInput
                    type="number"
                    value={data.blood_pressure.systolic[3] || ''}
                    readOnly
                    placeholder="Avg Systolic"
                  />
                </FormField>
                <FormField>
                  <FormLabel>Diastolic Avg</FormLabel>
                  <FormInput
                    type="number"
                    value={data.blood_pressure.diastolic[3] || ''}
                    readOnly
                    placeholder="Avg Diastolic"
                  />
                </FormField>
              </InputGroup>
            </FormField>
          </InputGroup>

          <InputGroup>
            <FormField>
              <FormLabel>Temperature (°C)</FormLabel>
              <FormInput
                type="number"
                value={data.temperature}
                hasError={hasError('temperature')}
                hasWarning={Boolean(fieldWarnings.temperature)}
                onChange={(e) => handleInputChange('temperature', e.target.value)}
                placeholder="Enter temperature"
              />
              {messagesFor('temperature')}
            </FormField>

            <FormField>
              <FormLabel>SPO2 (%)</FormLabel>
              <FormInput
                type="number"
                value={data.spo2}
                hasError={hasError('spo2')}
                onChange={(e) => handleInputChange('spo2', e.target.value)}
                placeholder="Enter SPO2"
              />
              {messagesFor('spo2')}
            </FormField>
          </InputGroup>
        </ContentBox>
      </SectionContainer>
      <SaveButton onClick={handleSave}>Save and Next</SaveButton>
      <SuccessModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title="Clinical Measurements Saved"
        message={`Clinical measurements have been successfully saved for Participant ID: ${participantId}`}
      />
    </ClinicalMeasurementsContainer>
  );
};

export default ClinicalMeasurements;
